import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Col } from "reactstrap";
import Toast from "../helpers/Toast";
import { AddNewStatusDTO } from "../models/Status/AddNewStatusDTO";
import { AssistanceService } from "../services/AssistanceService";
import ButtonComponent from "./ButtonComponent";
import { SelectItemsProps } from "./Select2Component";
import SelectComponent from "./SelectComponent";
import { TimeRegistrationModal } from "./TimeRegistration/TimeRegistrationModal";

export type ChangeStatusComponentProps = {
    close: () => void;
    onAdded: (newStatusName: string) => void;
    AssistanceId: number;
    EquipmentId?: number;
    IsOpen: boolean;
};

export default function ChangeStatusComponent(props: ChangeStatusComponentProps) {
    const intl = useIntl();
    const [statusList, setStatusList] = useState<SelectItemsProps[]>([]);
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const assistanceService = new AssistanceService();
    const [loadingInsert, setLoadingInsert] = useState<boolean>(false);
    const [loadingNextPossibleStatus, setLoadingNextPossibleStatus] = useState<boolean>(false);
    const [showExecutionsModal, setShowExecutionsModal] = useState<boolean>(false);
    const [executionErrorMessage, setExecutionErrorMessage] = useState<string>("");

    const getNextPossibleStatus = async () => {
        const list: SelectItemsProps[] = [];

        setLoadingNextPossibleStatus(true);
        var response = await assistanceService.GetPossibleStatus(props.AssistanceId);

        if (response.success === false) {
            Toast.Show("error", response.message);
            setLoadingNextPossibleStatus(false);
            return;
        }

        response.obj.forEach((element) => {
            var item: SelectItemsProps = {
                value: element.id.toString(),
                label: element.frontendName,
            };

            list.push(item);
        });

        if (list.length === 1) {
            setSelectedStatus(list[0].value);
        }

        setStatusList(list);
        setLoadingNextPossibleStatus(false);
    };

    const addNewStatus = async (): Promise<boolean> => {
        var data: AddNewStatusDTO = {
            assistanceId: props.AssistanceId,
            newStatusId: parseInt(selectedStatus),
        };

        var response = await assistanceService.AddNewStatus(data);
        if (response.success === false) {
            if (response.obj && response.obj.timeRegistrationRequired) {
                setExecutionErrorMessage(response.message);
                setShowExecutionsModal(true);
            } else {
                Toast.Show("error", response.message);
                props.close();
            }
            return false;
        }

        // Find the selected status name to pass back to parent
        const selectedStatusItem = statusList.find(s => s.value === selectedStatus);
        props.onAdded(selectedStatusItem?.label || "");
        Toast.Show("success", response.message);
        return true;
    };

    const addStatusClick = async () => {
        setLoadingInsert(true);
        var responseAdd = await addNewStatus();
        setLoadingInsert(false);
        if (responseAdd === true) {
            props.close();
        }
    };

    const handleExecutionAdded = async () => {
        setShowExecutionsModal(false);
        // After time registration, retry the status change
        const success = await addNewStatus();
        if (success) {
            props.close();
        }
    };

    const handleCloseExecutionsModal = () => {
        setShowExecutionsModal(false);
    };

    useEffect(() => {
        if (props.IsOpen === true && props.AssistanceId !== undefined) {
            getNextPossibleStatus();
        }
    }, [props.IsOpen]);

    return (
        <>
            <Dialog open={props.IsOpen}>
                <DialogTitle>{"Alterar Estado"}</DialogTitle>
                <DialogContent>
                    <SelectComponent
                        xl={12}
                        lg={12}
                        md={12}
                        sm={12}
                        xs={12}
                        style={{ width: "100%" }}
                        label={"Novo Estado"}
                        selectedItemValue={selectedStatus}
                        items={statusList}
                        onChange={(t: string) => {
                            setSelectedStatus(t);
                        }}
                        hideNotSelectedItem={statusList.length === 1}
                        blockItem={statusList.length === 1}
                        showLoadingSkeleton={loadingNextPossibleStatus}
                        loading={loadingNextPossibleStatus}
                    />
                </DialogContent>
                <DialogActions className="loginbuttons">
                    <div style={{ display: "flex", justifyContent: "space-between", margin: "10px", paddingLeft: "5px" }}>
                        <Col md={6} sm={6} xs={6} lg={6}>
                            <ButtonComponent
                                onClick={addStatusClick}
                                className="enteraccount"
                                style={{ width: "97%", borderRadius: "5px" }}
                                text={intl.formatMessage({ id: "ChangeStatusComponent.Update" })}
                                loading={loadingInsert}
                                disabled={loadingNextPossibleStatus}
                            />
                        </Col>
                        <Col md={6} sm={6} xs={6} lg={6}>
                            <ButtonComponent
                                onClick={props.close}
                                className="enteraccount"
                                style={{ width: "97%", borderRadius: "5px" }}
                                text={intl.formatMessage({ id: "ChangeStatusComponent.Cancel" })}
                                loading={loadingInsert}
                                disabled={loadingNextPossibleStatus}
                            />
                        </Col>
                    </div>
                </DialogActions>
            </Dialog>

            <TimeRegistrationModal
                isOpen={showExecutionsModal}
                onClose={handleCloseExecutionsModal}
                onComplete={handleExecutionAdded}
                assistanceId={props.AssistanceId}
                equipmentId={props.EquipmentId}
                errorMessage={executionErrorMessage}
            />
        </>
    );
}